import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/hooks';

import { getCourseById, getDailyChallengeForEditor } from 'store/course/actions';
import { getLessonById, removeLessonById } from 'store/lesson/actions';

import { currentCourseSelector } from 'store/course/selectors';
import { currentLessonSelector } from 'store/lesson/selectors';

import { showToast } from 'helpers/showToast';

import { getTranslation } from 'helpers/getTranslation';
import { getUnknownErrorMessage } from 'helpers/unknownErrorMessage';
import { getCourseEditorPathByType } from 'helpers/getLessonPathByCourseType';

import { useModal } from 'hooks/useModal';

import { DefaultModal } from 'components/DefaultModal';

import LessonProvider from './LessonProvider';

const LessonProviderContainer = () => {
  const { courseId, lessonId } = useParams();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isDailyChallengeLesson = pathname.startsWith('/daily-challenges/');

  const course = useSelector(currentCourseSelector);
  const lesson = useSelector(currentLessonSelector);

  const [loading, setLoading] = useState(true);

  const [
    confirmModalOpen,
    openConfirmModal,
    closeConfirmModal,
    confirmModalData,
  ] = useModal({
    defaultOpen: false,
  });

  useEffect(() => {
    setLoading(true);

    Promise.all([
      dispatch(
        isDailyChallengeLesson
          ? getDailyChallengeForEditor(courseId)
          : getCourseById(courseId),
      ),
      dispatch(getLessonById({ courseId, lessonId })),
    ]).finally(() => {
      setTimeout(() => {
        setLoading(false);
      }, 100);
    });
  }, [lessonId]);

  const handleConfirmRemoveClick = async () => {
    try {
      await dispatch(
        removeLessonById({ courseId, lessonId: confirmModalData.lessonId }),
      );

      closeConfirmModal();

      showToast({
        data: {
          variant: 'success',
          title: getTranslation('common.toast.remove_lesson.title'),
          text: getTranslation('common.toast.remove_lesson.text'),
        },
      });

      navigate(
        getCourseEditorPathByType({
          courseType: course.type,
          courseId: course.id,
        }),
      );
    } catch (error) {
      showToast({
        data: {
          variant: 'error',
          title: getTranslation('common.toast.error.title'),
          text: getUnknownErrorMessage(
            error,
            getTranslation('common.toast.error.text'),
          ),
        },
      });
    }
  };

  const handleRemoveClick = (data: any) =>
    openConfirmModal({
      lessonId: data.id,
      title: getTranslation('providers.lesson_provider.confirm_modal.title'),
      text: getTranslation('providers.lesson_provider.confirm_modal.text', {
        title: data.title || getTranslation('common.text.untitled'),
      }),
    });

  return (
    <>
      <LessonProvider
        loading={loading}
        lesson={lesson}
        onRemoveClick={handleRemoveClick}
      />
      <DefaultModal
        {...confirmModalData}
        open={confirmModalOpen}
        confirmText={getTranslation(
          'providers.lesson_provider.confirm_modal.confirm_text',
        )}
        onConfirmClick={handleConfirmRemoveClick}
        onCancelClick={closeConfirmModal}
        onClose={closeConfirmModal}
      />
    </>
  );
};

export default React.memo(LessonProviderContainer);
